import { useMemo } from 'react';

import type { ErrorLike } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { format, parseISO } from 'date-fns';

import { LISTING_GA4_STATS } from '@modules/listings/listingActivityQueries';

import { type Granularity, buildBuckets } from './chartUtils';
import {
  type ImpressionsSeries,
  PLACEMENTS,
  type PlacementKey,
  VIEW_CHANNELS,
  type ViewChannelKey,
} from './impressionsSeries';

// GA4 `item_list_id` values emitted by the public site map onto our placement
// keys. Unknown lists stay visible instead of inflating search results.
const PLACEMENT_BY_LIST_ID: Record<string, PlacementKey> = {
  search_results: 'search_results',
  similar_listings: 'similar_listings',
  agency_detail: 'agency_profile',
  agency_profile: 'agency_profile',
  landing_page: 'landing_page',
};

const toPlacement = (listId: string | null | undefined): PlacementKey => {
  if (listId != null && listId in PLACEMENT_BY_LIST_ID) {
    return PLACEMENT_BY_LIST_ID[listId]!;
  }
  return 'other';
};

const AI_SOURCES = [
  'chatgpt',
  'openai',
  'perplexity',
  'copilot',
  'gemini',
  'claude',
];
const SOCIAL_SOURCES = [
  'fb',
  'facebook',
  'insta',
  'linkedin',
  'tiktok',
  'pinterest',
  'youtube',
  'twitter',
  'threads',
];
const PAID_MEDIUMS = ['cpc', 'ppc', 'cpm', 'display', 'native', 'banner'];
const PAID_SOURCES = ['outbrain', 'taboola'];

// For `view_item` rows the datawarehouse flow packs the channel that
// ORIGINALLY acquired the viewer (GA4's `traffic_source`, frozen on the
// user's first-ever visit — not necessarily how they reached this listing)
// as `source|medium` into `item_list_id`. Map it to a friendly channel;
// empty means GA4 had no attribution for that view.
const toViewChannel = (listId: string | null | undefined): ViewChannelKey => {
  const [rawSource = '', rawMedium = ''] = (listId ?? '').split('|');
  const source = rawSource.toLowerCase();
  const medium = rawMedium.toLowerCase();
  if (source === '' && medium === '') {
    return 'other';
  }
  if (
    medium === 'ai' ||
    medium === 'ai-assistant' ||
    AI_SOURCES.some(s => source.includes(s))
  ) {
    return 'ai';
  }
  if (medium === 'organic') {
    return 'organic';
  }
  if (
    medium === 'email' ||
    source.includes('email') ||
    source === 'sendgrid' ||
    source === 'newsletter'
  ) {
    return 'email';
  }
  if (
    medium === 'cpc' ||
    medium === 'ppc' ||
    medium.startsWith('paid') ||
    PAID_MEDIUMS.includes(medium) ||
    PAID_SOURCES.some(s => source.includes(s))
  ) {
    return 'paid';
  }
  if (medium === 'social' || SOCIAL_SOURCES.some(s => source.includes(s))) {
    return 'social';
  }
  if (medium === 'referral' || medium === 'partners') {
    return 'referral';
  }
  if (source === '(direct)' || source === 'direct') {
    return 'direct';
  }
  return 'other';
};

type Ga4Row = {
  day: string;
  event_name: string;
  item_list_id?: string | null;
  count: number;
};

const buildFromRows = (
  rows: readonly Ga4Row[],
  start: Date,
  end: Date,
  granularity: Granularity,
): ImpressionsSeries => {
  const buckets = buildBuckets(start, end, granularity);
  const perPlacement = new Map<PlacementKey, Map<string, number>>(
    PLACEMENTS.map(placement => [placement.key, new Map()]),
  );
  const perChannel = new Map<ViewChannelKey, Map<string, number>>(
    VIEW_CHANNELS.map(channel => [channel.key, new Map()]),
  );

  for (const row of rows) {
    const date = parseISO(row.day);
    if (date < buckets.start || date > buckets.end) {
      continue;
    }
    const key = buckets.keyOf(date);
    if (row.event_name === 'view_item_list') {
      const bucket = perPlacement.get(toPlacement(row.item_list_id))!;
      bucket.set(key, (bucket.get(key) ?? 0) + row.count);
    } else if (row.event_name === 'view_item') {
      const bucket = perChannel.get(toViewChannel(row.item_list_id))!;
      bucket.set(key, (bucket.get(key) ?? 0) + row.count);
    }
  }

  const barSeries = PLACEMENTS.map(placement => ({
    key: placement.key,
    color: placement.color,
    data: buckets.keys.map(
      key => perPlacement.get(placement.key)?.get(key) ?? 0,
    ),
  }));
  const viewsSeries = VIEW_CHANNELS.map(channel => ({
    key: channel.key,
    color: channel.color,
    data: buckets.keys.map(key => perChannel.get(channel.key)?.get(key) ?? 0),
  }));
  const viewsTotal = buckets.keys.map((_, index) =>
    viewsSeries.reduce((sum, series) => sum + series.data[index]!, 0),
  );
  const totalImpressions = barSeries.reduce(
    (sum, series) => sum + series.data.reduce((a, b) => a + b, 0),
    0,
  );
  const totalViews = viewsTotal.reduce((a, b) => a + b, 0);

  return {
    labels: buckets.labels,
    barSeries,
    viewsSeries,
    viewsTotal,
    totalImpressions,
    totalViews,
  };
};

export type ListingImpressions = ImpressionsSeries & {
  loading: boolean;
  error?: ErrorLike;
};

// Real GA4 impressions / listing views for a listing over [start, end]. The
// `listing_ga4_stats` action authorizes the lot under the caller's token and
// resolves its GA4 id server-side; an unmapped listing returns zeros. Pass
// `skip` where the tenant has no live portal (only CH publishes listings on
// RealAdvisor today) so we don't scan GA4 for counts that are always zero.
export const useListingImpressions = (
  lotId: string,
  start: Date,
  end: Date,
  granularity: Granularity,
  { skip = false }: { skip?: boolean } = {},
): ListingImpressions => {
  const { data, loading, error } = useQuery(LISTING_GA4_STATS, {
    variables: {
      lotId,
      dateFrom: format(start, 'yyyy-MM-dd'),
      dateTo: format(end, 'yyyy-MM-dd'),
    },
    skip,
  });

  return useMemo(() => {
    const rows = data?.listing_ga4_stats?.data ?? [];
    return { ...buildFromRows(rows, start, end, granularity), loading, error };
  }, [data, loading, error, start, end, granularity]);
};
